import { IWatrRequestAuditLog } from '@nkr/models/index';
import { AdvancedWorkflowStatus } from '@nkr/enums/index';

import type { CSSProperties } from 'react';

export const AUDIT_FILTER_NEUTRAL_COLORS = {
    badgeBackground: '#f5f5f5',
    badgeBorder: '#adb5bd',
    badgeColor: '#495057'
};

export const WATR_LOG_ACTION_TYPE = {
    Approval: 1,
    Denial: 2,
    Reimbursement: 3,
    PayStubVerify: 4,
    TaxVerify: 5,
    MilesOverridden: 6,
    MealsOverridden: 7,
    CarServiceOverridden: 8,
    DependentCareOverriden: 9,
    TravelReceiptsVerified: 10,
    BankingReviewFlags: 11,
    DonorIntakeInvitationSent: 12,
    AdminDashboardAccess: 13,
    AdminAccessTokenGranted: 14,
    AdvancedReimbursementWorkflow: 15,
    Donation: 16,
    ManualNote: 17,
    AdvancedReimbursementInvitationSent: 18,
    TravelReceiptUploaded: 19,
    AdvancedReimbursementPreOpScheduledNoticeSent: 20,
    UpdateRequest: 100,
    AuditUpdateRequest: 110
} as const;

export const WATR_LOG_REQUEST_TYPE = {
    Wage: 1,
    Travel: 2,
    ComplicationWage: 3,
    ComplicationTravel: 4,
    ParentRequest: 99
} as const;

export const AUDIT_ACTION_FILTER_ALL = 'all';
export const AUDIT_ACTION_FILTER_UNCLASSIFIED = 'unclassified';

export const AUDIT_ACTION_CATEGORY = {
    ApprovalProcess: 'approvalProcess',
    TravelVerification: 'travelVerification',
    Reimbursement: 'reimbursement',
    AdvancedReimbursement: 'advancedReimbursement',
    DonationIntake: 'donationIntake',
    AdminAccess: 'adminAccess',
    Notes: 'notes'
} as const;

export type AuditActionCategoryId =
    | typeof AUDIT_ACTION_CATEGORY[keyof typeof AUDIT_ACTION_CATEGORY]
    | typeof AUDIT_ACTION_FILTER_ALL
    | typeof AUDIT_ACTION_FILTER_UNCLASSIFIED;

export const AUDIT_APPROVAL_SUB_FILTER = {
    All: 'all',
    Declined: 'declined',
    Verified: 'verified',
    Banking: 'banking'
} as const;

export type AuditApprovalSubFilterId = typeof AUDIT_APPROVAL_SUB_FILTER[keyof typeof AUDIT_APPROVAL_SUB_FILTER];

const ADVANCED_WORKFLOW_TRANSITION_PREFIX = 'AdvancedWorkflowTransition:';

export const ADVANCED_WORKFLOW_AUDIT_LABEL_BY_CODE: Record<number, string> = {
    [AdvancedWorkflowStatus.Unknown]: 'Unknown',
    [AdvancedWorkflowStatus.InviteFailed]: 'Invitation could not be sent',
    [AdvancedWorkflowStatus.AwaitingDonorProfile]: 'Profile Incomplete',
    [AdvancedWorkflowStatus.AwaitingApplicationConfirmation]: 'Awaiting Application Confirmation',
    [AdvancedWorkflowStatus.AwaitingNkrReview]: 'Awaiting NKR Review',
    [AdvancedWorkflowStatus.AwaitingDonorSubmission]: 'Awaiting Donor Submission',
    [AdvancedWorkflowStatus.PendingNkrVerification]: 'Awaiting NKR Verification',
    [AdvancedWorkflowStatus.ReadyToReconcile]: 'Ready to Reconcile',
    [AdvancedWorkflowStatus.OnHold]: 'On Hold',
    [AdvancedWorkflowStatus.Complete]: 'Closed',
    [AdvancedWorkflowStatus.Cancelled]: 'Cancelled',
    [AdvancedWorkflowStatus.Declined]: 'Declined',
};

function formatAdvancedWorkflowStatusLabel(status: AdvancedWorkflowStatus): string {
    return ADVANCED_WORKFLOW_AUDIT_LABEL_BY_CODE[status] ?? String(status);
}

export function formatAdvancedWorkflowTransitionLogMessage(logMessage: string | undefined): string | null {
    if (!logMessage || !logMessage.startsWith(ADVANCED_WORKFLOW_TRANSITION_PREFIX)) {
        return null;
    }

    const body = logMessage.substring(ADVANCED_WORKFLOW_TRANSITION_PREFIX.length).trim();
    const segments = body.split(';').map(part => part.trim()).filter(part => part.length > 0);

    const reasonSegment = segments.find(part => part.toLowerCase().startsWith('reason='));
    if (reasonSegment) {
        const reason = reasonSegment.substring('reason='.length).trim();
        if (reason.length > 0) {
            return reason;
        }
    }

    const transitionSegment = segments[0] ?? '';
    const parts = transitionSegment.split('->').map(part => part.trim());
    if (parts.length === 2) {
        const fromRaw = parts[0] as keyof typeof AdvancedWorkflowStatus;
        const toRaw = parts[1] as keyof typeof AdvancedWorkflowStatus;
        const from = AdvancedWorkflowStatus[fromRaw];
        const to = AdvancedWorkflowStatus[toRaw];
        if (typeof from === 'number' && typeof to === 'number' && from !== to) {
            return `${formatAdvancedWorkflowStatusLabel(from)} → ${formatAdvancedWorkflowStatusLabel(to)}`;
        }
    }

    return 'Advanced reimbursement workflow updated';
}

function formatIntakeInviteLogMessageForDisplay(logMessage: string): string {
    const trimmed = logMessage.trim();
    if (!trimmed.startsWith('DonorIntakeInvite:') && !trimmed.startsWith('AdvancedReimbursementInvite:')) {
        return trimmed;
    }
    return trimmed
        .replace(/\s+registration\s+\d+/gi, '')
        .replace(/\s+donor\s+\d+/gi, '')
        .trim();
}

export function formatAuditLogDetailMessage(log: IWatrRequestAuditLog): string | undefined {
    const raw = (log.logMessage || '').trim();
    if (!raw) {
        return undefined;
    }

    const workflow = formatAdvancedWorkflowTransitionLogMessage(raw);
    if (workflow) {
        return workflow;
    }

    if (raw.startsWith('DonorIntakeInvite:') || raw.startsWith('AdvancedReimbursementInvite:')) {
        return formatIntakeInviteLogMessageForDisplay(raw);
    }

    return raw;
}

export interface IAuditActionCategoryDefinition {
    id: AuditActionCategoryId;
    label: string;
    cellShortLabel: string;
    filterChipLabel: string;
    actionTypeIds: number[];
    badgeBackground: string;
    badgeBorder: string;
    badgeColor: string;
}

export const AUDIT_ACTION_CATEGORIES: IAuditActionCategoryDefinition[] = [
    {
        id: AUDIT_ACTION_CATEGORY.ApprovalProcess,
        label: 'Lost wages (LWR) — verify steps, approval, banking, status, donor updates',
        cellShortLabel: 'LWR',
        filterChipLabel: 'LWR',
        actionTypeIds: [
            WATR_LOG_ACTION_TYPE.Approval,
            WATR_LOG_ACTION_TYPE.Denial,
            WATR_LOG_ACTION_TYPE.PayStubVerify,
            WATR_LOG_ACTION_TYPE.TaxVerify,
            WATR_LOG_ACTION_TYPE.BankingReviewFlags
        ],
        badgeBackground: '#e8f4fc',
        badgeBorder: '#5bc0de',
        badgeColor: '#245269'
    },
    {
        id: AUDIT_ACTION_CATEGORY.TravelVerification,
        label: 'Travel (DER) — verify steps, receipt review, expense overrides',
        cellShortLabel: 'Travel',
        filterChipLabel: 'Travel',
        actionTypeIds: [
            WATR_LOG_ACTION_TYPE.TravelReceiptsVerified,
            WATR_LOG_ACTION_TYPE.TravelReceiptUploaded,
            WATR_LOG_ACTION_TYPE.MilesOverridden,
            WATR_LOG_ACTION_TYPE.MealsOverridden,
            WATR_LOG_ACTION_TYPE.CarServiceOverridden,
            WATR_LOG_ACTION_TYPE.DependentCareOverriden
        ],
        badgeBackground: '#f3e8ff',
        badgeBorder: '#9b59b6',
        badgeColor: '#5b2c6f'
    },
    {
        id: AUDIT_ACTION_CATEGORY.Reimbursement,
        label: 'Reimbursement',
        cellShortLabel: 'Reimbursement',
        filterChipLabel: 'Reimburse',
        actionTypeIds: [WATR_LOG_ACTION_TYPE.Reimbursement],
        badgeBackground: '#e8f8f0',
        badgeBorder: '#3c763d',
        badgeColor: '#2e6b32'
    },
    {
        id: AUDIT_ACTION_CATEGORY.AdvancedReimbursement,
        label: 'Advanced reimbursement',
        cellShortLabel: 'Advanced',
        filterChipLabel: 'Advanced',
        actionTypeIds: [
            WATR_LOG_ACTION_TYPE.AdvancedReimbursementWorkflow,
            WATR_LOG_ACTION_TYPE.AdvancedReimbursementInvitationSent,
            WATR_LOG_ACTION_TYPE.AdvancedReimbursementPreOpScheduledNoticeSent
        ],
        badgeBackground: '#fff4e5',
        badgeBorder: '#f0ad4e',
        badgeColor: '#8a5a00'
    },
    {
        id: AUDIT_ACTION_CATEGORY.DonationIntake,
        label: 'Donation',
        cellShortLabel: 'Donation',
        filterChipLabel: 'Donation',
        actionTypeIds: [
            WATR_LOG_ACTION_TYPE.DonorIntakeInvitationSent,
            WATR_LOG_ACTION_TYPE.Donation
        ],
        badgeBackground: '#fce8f3',
        badgeBorder: '#d9534f',
        badgeColor: '#843534'
    },
    {
        id: AUDIT_ACTION_CATEGORY.AdminAccess,
        label: 'Admin Access',
        cellShortLabel: 'Admin Access',
        filterChipLabel: 'Admin Access',
        actionTypeIds: [
            WATR_LOG_ACTION_TYPE.AdminDashboardAccess,
            WATR_LOG_ACTION_TYPE.AdminAccessTokenGranted,
            WATR_LOG_ACTION_TYPE.AuditUpdateRequest
        ],
        badgeBackground: '#eef0f4',
        badgeBorder: '#6c757d',
        badgeColor: '#343a40'
    },
    {
        id: AUDIT_ACTION_CATEGORY.Notes,
        label: 'Notes',
        cellShortLabel: 'Notes',
        filterChipLabel: 'Notes',
        actionTypeIds: [WATR_LOG_ACTION_TYPE.ManualNote],
        badgeBackground: '#fffbe6',
        badgeBorder: '#d4b106',
        badgeColor: '#614700'
    }
];

const ACTION_TYPE_TO_CATEGORY = new Map<number, IAuditActionCategoryDefinition>();
AUDIT_ACTION_CATEGORIES.forEach(cat => {
    cat.actionTypeIds.forEach(id => ACTION_TYPE_TO_CATEGORY.set(id, cat));
});

const DONOR_PORTAL_UPDATE_MESSAGES = new Set([
    'Reimbursement information updated.',
    'Travel details updated.',
    'Donor profile updated.'
]);

const getCategoryDefinitionById = (id: AuditActionCategoryId): IAuditActionCategoryDefinition | undefined =>
    AUDIT_ACTION_CATEGORIES.find(c => c.id === id);

const isDonorPortalUpdateLog = (log: IWatrRequestAuditLog, actionTypeId: number): boolean => {
    if (actionTypeId !== WATR_LOG_ACTION_TYPE.UpdateRequest) {
        return false;
    }
    const msg = (log.logMessage || '').trim();
    if (DONOR_PORTAL_UPDATE_MESSAGES.has(msg)) {
        return true;
    }
    return msg.startsWith('Week ') && msg.includes('requested with status:');
};

const isTravelRequestType = (log: IWatrRequestAuditLog): boolean => {
    const requestType = log.logRequestType;
    return requestType === WATR_LOG_REQUEST_TYPE.Travel
        || requestType === WATR_LOG_REQUEST_TYPE.ComplicationTravel;
};

export const resolveCategoryForLog = (log: IWatrRequestAuditLog): IAuditActionCategoryDefinition | null => {
    const actionTypeId = resolveLogActionTypeId(log);
    if (actionTypeId == null) {
        return null;
    }
    if (actionTypeId === WATR_LOG_ACTION_TYPE.AuditUpdateRequest) {
        return getCategoryDefinitionById(AUDIT_ACTION_CATEGORY.AdminAccess) ?? null;
    }
    if (
        isDonorPortalUpdateLog(log, actionTypeId)
        || actionTypeId === WATR_LOG_ACTION_TYPE.UpdateRequest
    ) {
        return getCategoryDefinitionById(AUDIT_ACTION_CATEGORY.ApprovalProcess) ?? null;
    }
    if (actionTypeId === WATR_LOG_ACTION_TYPE.Denial) {
        if (isTravelRequestType(log)) {
            return getCategoryDefinitionById(AUDIT_ACTION_CATEGORY.TravelVerification) ?? null;
        }
        return getCategoryDefinitionById(AUDIT_ACTION_CATEGORY.ApprovalProcess) ?? null;
    }
    return ACTION_TYPE_TO_CATEGORY.get(actionTypeId) ?? null;
};

const DETAIL_LABEL_BY_ACTION_TYPE: Record<number, string> = {
    [WATR_LOG_ACTION_TYPE.Approval]: 'Lost wages amount approved',
    [WATR_LOG_ACTION_TYPE.Denial]: 'Request declined',
    [WATR_LOG_ACTION_TYPE.Reimbursement]: 'Reimbursement',
    [WATR_LOG_ACTION_TYPE.PayStubVerify]: 'Pay stub verified',
    [WATR_LOG_ACTION_TYPE.TaxVerify]: 'Tax verified',
    [WATR_LOG_ACTION_TYPE.MilesOverridden]: 'Mileage override',
    [WATR_LOG_ACTION_TYPE.MealsOverridden]: 'Meals override',
    [WATR_LOG_ACTION_TYPE.CarServiceOverridden]: 'Car service override',
    [WATR_LOG_ACTION_TYPE.DependentCareOverriden]: 'Dependent care override',
    [WATR_LOG_ACTION_TYPE.TravelReceiptsVerified]: 'Receipts verified',
    [WATR_LOG_ACTION_TYPE.TravelReceiptUploaded]: 'Receipt uploaded',
    [WATR_LOG_ACTION_TYPE.BankingReviewFlags]: 'Banking review',
    [WATR_LOG_ACTION_TYPE.DonorIntakeInvitationSent]: 'Intake invitation',
    [WATR_LOG_ACTION_TYPE.AdvancedReimbursementInvitationSent]: 'Advanced reimbursement invitation',
    [WATR_LOG_ACTION_TYPE.AdvancedReimbursementPreOpScheduledNoticeSent]: 'Pre-op scheduled notice',
    [WATR_LOG_ACTION_TYPE.AdminDashboardAccess]: 'Dashboard link generated',
    [WATR_LOG_ACTION_TYPE.AdminAccessTokenGranted]: 'Granted Profile Access',
    [WATR_LOG_ACTION_TYPE.AdvancedReimbursementWorkflow]: 'Workflow step',
    [WATR_LOG_ACTION_TYPE.Donation]: 'Donation',
    [WATR_LOG_ACTION_TYPE.ManualNote]: 'Manual note',
    [WATR_LOG_ACTION_TYPE.UpdateRequest]: 'Request updated',
    [WATR_LOG_ACTION_TYPE.AuditUpdateRequest]: 'Audit record updated'
};

export type AuditActionDetailTone = 'declined' | 'approved' | 'neutral' | 'warning';

export interface IAuditActionDisplayParts {
    category: IAuditActionCategoryDefinition | null;
    detailLabel: string;
    detailTone: AuditActionDetailTone;
    isClassified: boolean;
}

export const isLegacyAdvancedWorkflowLog = (log: IWatrRequestAuditLog): boolean =>
    !!log.logMessage && log.logMessage.startsWith(ADVANCED_WORKFLOW_TRANSITION_PREFIX);

const inferLogActionTypeFromMessage = (log: IWatrRequestAuditLog): number | null => {
    const msg = (log.logMessage || '').trim();
    if (msg.length > 0) {
        const lower = msg.toLowerCase();
        if (lower.startsWith('admin dashboard access')) {
            return WATR_LOG_ACTION_TYPE.AdminDashboardAccess;
        }
        if (lower.includes('access token') && lower.includes('granted')) {
            return WATR_LOG_ACTION_TYPE.AdminAccessTokenGranted;
        }
    }

    const description = (log.logActionTypeDescription || '').trim().toLowerCase();
    if (description === 'dashboard access') {
        return WATR_LOG_ACTION_TYPE.AdminDashboardAccess;
    }
    if (description === 'access granted') {
        return WATR_LOG_ACTION_TYPE.AdminAccessTokenGranted;
    }

    return null;
};

export const resolveLogActionTypeId = (log: IWatrRequestAuditLog): number | null => {
    const fromMessage = inferLogActionTypeFromMessage(log);
    if (fromMessage != null) {
        return fromMessage;
    }
    const raw = log.logActionType;
    if (raw != null && raw > 0) {
        return raw;
    }
    if (isLegacyAdvancedWorkflowLog(log)) {
        return WATR_LOG_ACTION_TYPE.AdvancedReimbursementWorkflow;
    }
    return null;
};

export const getCategoryForLog = (log: IWatrRequestAuditLog): IAuditActionCategoryDefinition | null =>
    resolveCategoryForLog(log);

export const getActionDisplayParts = (log: IWatrRequestAuditLog): IAuditActionDisplayParts => {
    const actionTypeId = resolveLogActionTypeId(log);
    const category = resolveCategoryForLog(log);

    if (actionTypeId == null) {
        return {
            category: null,
            detailLabel: '',
            detailTone: 'neutral',
            isClassified: false
        };
    }

    const fromApi = (log.logActionTypeDescription || '').trim();
    let detailLabel = DETAIL_LABEL_BY_ACTION_TYPE[actionTypeId] || fromApi || `Action ${actionTypeId}`;
    let detailTone: AuditActionDetailTone = 'neutral';

    if (actionTypeId === WATR_LOG_ACTION_TYPE.UpdateRequest) {
        const msg = (log.logMessage || '').trim();
        if (msg.length > 0) {
            detailLabel = msg;
        }
    }

    if (actionTypeId === WATR_LOG_ACTION_TYPE.Denial) {
        const msg = (log.logMessage || '').trim();
        if (/^Declined .+ cleared/i.test(msg)) {
            detailLabel = msg;
        } else if (/^Week \d+ declined/i.test(msg)) {
            detailLabel = 'Week reimbursement declined';
        } else {
            detailLabel = 'Request declined';
        }
        detailTone = 'declined';
    } else if (
        actionTypeId === WATR_LOG_ACTION_TYPE.DonorIntakeInvitationSent
        || actionTypeId === WATR_LOG_ACTION_TYPE.AdvancedReimbursementInvitationSent
    ) {
        const msg = formatIntakeInviteLogMessageForDisplay(log.logMessage || '');
        if (msg.length > 0) {
            detailLabel = msg;
        }
    } else if (actionTypeId === WATR_LOG_ACTION_TYPE.AdvancedReimbursementWorkflow) {
        const formatted = formatAdvancedWorkflowTransitionLogMessage(log.logMessage || '');
        if (formatted) {
            detailLabel = formatted;
        }
    } else if (
        actionTypeId === WATR_LOG_ACTION_TYPE.Approval
        || actionTypeId === WATR_LOG_ACTION_TYPE.PayStubVerify
        || actionTypeId === WATR_LOG_ACTION_TYPE.TaxVerify
        || actionTypeId === WATR_LOG_ACTION_TYPE.TravelReceiptsVerified
    ) {
        if (actionTypeId === WATR_LOG_ACTION_TYPE.Approval) {
            const msg = (log.logMessage || '').trim();
            if (/^Week \d+ approved/i.test(msg)) {
                detailLabel = 'Week reimbursement approved';
            }
        }
        detailTone = 'approved';
    } else if (actionTypeId === WATR_LOG_ACTION_TYPE.BankingReviewFlags) {
        detailTone = 'warning';
    }

    return {
        category,
        detailLabel,
        detailTone,
        isClassified: category != null
    };
};

const ACTIVITY_DETAIL_SUPPRESSED_WHEN_STATUS_CHANGE = new Set<number>([
    WATR_LOG_ACTION_TYPE.Approval,
    WATR_LOG_ACTION_TYPE.PayStubVerify,
    WATR_LOG_ACTION_TYPE.TaxVerify,
    WATR_LOG_ACTION_TYPE.TravelReceiptsVerified,
    WATR_LOG_ACTION_TYPE.BankingReviewFlags,
    WATR_LOG_ACTION_TYPE.Reimbursement,
    WATR_LOG_ACTION_TYPE.MilesOverridden,
    WATR_LOG_ACTION_TYPE.MealsOverridden,
    WATR_LOG_ACTION_TYPE.CarServiceOverridden,
    WATR_LOG_ACTION_TYPE.DependentCareOverriden,
    WATR_LOG_ACTION_TYPE.Donation
]);

export const shouldShowActivityDetailLabel = (
    log: IWatrRequestAuditLog,
    parts: IAuditActionDisplayParts,
    hasStatusChange: boolean
): boolean => {
    if (!parts.detailLabel.length) {
        return false;
    }

    const actionTypeId = resolveLogActionTypeId(log);
    if (actionTypeId === WATR_LOG_ACTION_TYPE.Denial) {
        return true;
    }
    if (actionTypeId === WATR_LOG_ACTION_TYPE.UpdateRequest) {
        return true;
    }
    if (actionTypeId === WATR_LOG_ACTION_TYPE.DonorIntakeInvitationSent) {
        return true;
    }
    if (actionTypeId === WATR_LOG_ACTION_TYPE.AdvancedReimbursementInvitationSent) {
        return true;
    }
    if (actionTypeId === WATR_LOG_ACTION_TYPE.ManualNote) {
        return true;
    }
    if (actionTypeId === WATR_LOG_ACTION_TYPE.AdvancedReimbursementWorkflow) {
        return true;
    }

    if (hasStatusChange && actionTypeId != null && ACTIVITY_DETAIL_SUPPRESSED_WHEN_STATUS_CHANGE.has(actionTypeId)) {
        return false;
    }

    if (
        parts.category?.id === AUDIT_ACTION_CATEGORY.DonationIntake
        && actionTypeId === WATR_LOG_ACTION_TYPE.Donation
        && parts.detailLabel === DETAIL_LABEL_BY_ACTION_TYPE[WATR_LOG_ACTION_TYPE.Donation]
    ) {
        return false;
    }

    return true;
};

const APPROVAL_SUB_FILTER_TYPES: Record<AuditApprovalSubFilterId, number[] | null> = {
    [AUDIT_APPROVAL_SUB_FILTER.All]: null,
    [AUDIT_APPROVAL_SUB_FILTER.Declined]: [WATR_LOG_ACTION_TYPE.Denial],
    [AUDIT_APPROVAL_SUB_FILTER.Verified]: [
        WATR_LOG_ACTION_TYPE.Approval,
        WATR_LOG_ACTION_TYPE.PayStubVerify,
        WATR_LOG_ACTION_TYPE.TaxVerify,
        WATR_LOG_ACTION_TYPE.TravelReceiptsVerified
    ],
    [AUDIT_APPROVAL_SUB_FILTER.Banking]: [WATR_LOG_ACTION_TYPE.BankingReviewFlags]
};

export const logMatchesActionCategoryFilter = (
    log: IWatrRequestAuditLog,
    categoryFilter: AuditActionCategoryId,
    approvalSubFilter: AuditApprovalSubFilterId
): boolean => {
    if (categoryFilter === AUDIT_ACTION_FILTER_ALL) {
        return true;
    }

    const parts = getActionDisplayParts(log);

    if (categoryFilter === AUDIT_ACTION_FILTER_UNCLASSIFIED) {
        return !parts.isClassified;
    }

    const resolvedCategory = resolveCategoryForLog(log);
    if (resolvedCategory?.id !== categoryFilter) {
        return false;
    }

    const actionTypeId = resolveLogActionTypeId(log);
    if (actionTypeId == null) {
        return false;
    }

    if (categoryFilter === AUDIT_ACTION_CATEGORY.ApprovalProcess && approvalSubFilter !== AUDIT_APPROVAL_SUB_FILTER.All) {
        const allowed = APPROVAL_SUB_FILTER_TYPES[approvalSubFilter];
        if (allowed && !allowed.includes(actionTypeId)) {
            return false;
        }
    }

    return true;
};

export type CategoryBadgeEmphasis = 'table' | 'chip-active' | 'chip-inactive';

export const getCategoryBadgeStyle = (
    cat: IAuditActionCategoryDefinition,
    emphasis: CategoryBadgeEmphasis
): CSSProperties => {
    const style: CSSProperties = {
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 600,
        padding: '4px 8px',
        borderRadius: 3,
        backgroundColor: cat.badgeBackground,
        borderLeft: `3px solid ${cat.badgeBorder}`,
        color: cat.badgeColor,
        lineHeight: 1.35,
        border: 'none',
        borderTop: 'none',
        borderRight: 'none',
        borderBottom: 'none'
    };

    if (emphasis === 'table') {
        return {
            ...style,
            maxWidth: '100%',
            whiteSpace: 'normal'
        };
    }

    if (emphasis === 'chip-active') {
        return {
            ...style,
            boxShadow: `inset 0 0 0 1px ${cat.badgeBorder}`
        };
    }

    return {
        ...style,
        opacity: 0.78
    };
};

export const getNeutralFilterChipStyle = (emphasis: CategoryBadgeEmphasis): CSSProperties => {
    const n = AUDIT_FILTER_NEUTRAL_COLORS;
    const style: CSSProperties = {
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 600,
        padding: '4px 8px',
        borderRadius: 3,
        backgroundColor: n.badgeBackground,
        borderLeft: `3px solid ${n.badgeBorder}`,
        color: n.badgeColor,
        lineHeight: 1.35,
        border: 'none',
        borderTop: 'none',
        borderRight: 'none',
        borderBottom: 'none'
    };

    if (emphasis === 'chip-active') {
        return {
            ...style,
            backgroundColor: '#e8f4fc',
            borderLeft: '3px solid #5bc0de',
            color: '#245269',
            boxShadow: 'inset 0 0 0 1px #5bc0de'
        };
    }

    if (emphasis === 'chip-inactive') {
        return { ...style, opacity: 0.88 };
    }

    return style;
};

export const getDetailToneStyles = (tone: AuditActionDetailTone): { color: string; fontWeight: number } => {
    switch (tone) {
        case 'declined':
            return { color: '#a94442', fontWeight: 600 };
        case 'approved':
            return { color: '#3c763d', fontWeight: 600 };
        case 'warning':
            return { color: '#8a6d3b', fontWeight: 600 };
        default:
            return { color: '#333', fontWeight: 600 };
    }
};
